/* eslint-disable id-length */
import { ColorScheme, ColorSchemeHelper, RGB, RGBHelper } from './common';
import { CMYK, CMYKHelper } from './cmyk';
import { HSL, HSLHelper } from './hsl';
import { HSV, HSVHelper } from './hsv';

export type Color = CMYK | HSL | HSV | RGB;

class ColorSchemeHelpers {
    public rgb = new RGBHelper();

    public hsl = new HSLHelper();

    public hsv = new HSVHelper();

    public cmyk = new CMYKHelper();

    public all() {
        return [this.rgb, this.hsl, this.hsv, this.cmyk];
    }

    public byName(scheme: ColorScheme): ColorSchemeHelper<Color> {
        return this[scheme] as ColorSchemeHelper<Color>;
    }
}

export const colorSchemeHelpers = new ColorSchemeHelpers();

export function getColorScheme(color: Color | undefined): ColorScheme | undefined {
    if (!color) {
        return undefined;
    }

    const helper = colorSchemeHelpers.all().filter(h => h.isMatch(color))[0];


    if (!helper) {
        return undefined;
    } else {
        return helper.scheme;
    }
}

/**
 * Parses common web string representations of colors and returns a Color object.
 * Does not support parsing word versions of web colors (e.g. 'red', 'green', etc).
 * Any component that expects a percentage can be expressed either a decimal from 0 to 1
 * or as a percentage from 0% to 100%.
 * Its slightly forgiving and will clamp values to their bounds.
 *
 * e.g.
 *     #ff0000                 red
 *     #ff00007f               red with 50% opacity
 *     rgb(255, 0, 0)          red
 *     rgba(255, 0, 0, .5)     red with 50% opacity
 *     hsl(0, 100%, .5)        red
 *     hsla(0, 1, .5, .5)   red with 50% opacity
 *     hsv(0, 1, 1)            red
 *     hsv(0, 1, 1, 50%)       red with 50% opacity
 *     device-cmyk(1, 0, 0, 0) will return cyan even though device-cmyk is still just proposed for css4
 *
 * @param color - color string to parse
 * @returns Color | undefined
 */
export function parseColor(color: string): Color | undefined {
    for (const helper of colorSchemeHelpers.all()) {
        const result = helper.parse(color);
        if (result) {
            return result;
        }
    }

    return undefined;
}

export function isValidColorScheme(scheme: string | undefined): scheme is ColorScheme {
    switch (scheme) {
        case 'rgb':
        case 'hsl':
        case 'hsv':
        case 'cmyk':
            return true;

        default:
            return false;
    }
}

export function convertToScheme(color: Color | string, targetScheme: 'cmyk'): CMYK | undefined;
export function convertToScheme(color: Color | string, targetScheme: 'hsl'): HSL | undefined;
export function convertToScheme(color: Color | string, targetScheme: 'hsv'): HSV | undefined;
export function convertToScheme(color: Color | string, targetScheme: 'rgb'): RGB | undefined;
export function convertToScheme(color: undefined, targetScheme: ColorScheme): undefined;
export function convertToScheme(color: Color | string, targetScheme: undefined): undefined;
export function convertToScheme(
    color: Color | string | undefined,
    targetScheme: ColorScheme | undefined,
): CMYK | HSL | HSV | RGB | undefined;
export function convertToScheme(
    color: Color | string | undefined,
    targetScheme: ColorScheme | undefined,
): CMYK | HSL | HSV | RGB | undefined {
    if (!color) {
        return undefined;
    }

    const oldValues = typeof color === 'string' ? parseColor(color) : color;
    if (!oldValues) {
        return undefined;
    }
    const oldScheme = getColorScheme(oldValues);

    if (typeof oldScheme !== 'string') {
        throw new Error(
            `Could not convert colour: Could not determine colour scheme of colour: ${
                typeof color === 'string' ? color : JSON.stringify(color)
            }`,
        );
    }

    if (!isValidColorScheme(targetScheme)) {
        throw new Error(`Could not convert colour: Invalid target scheme: ${targetScheme}`);
    }

    const fromHelper = colorSchemeHelpers.byName(oldScheme);
    const toHelper = colorSchemeHelpers.byName(targetScheme);

    const rgb = fromHelper.toRgb(oldValues, { round: false });

    const result = toHelper.fromRgb(rgb);

    return result;
}

export function areColorsEqual(a: Color | null | undefined, b: Color | null | undefined): boolean {
    const aScheme = getColorScheme(a || undefined);
    const bScheme = getColorScheme(b || undefined);

    if (!aScheme) {
        if (!bScheme) {
            return true;
        }
        return colorSchemeHelpers.byName(bScheme).areEqual(a, b);
    } else if (!bScheme) {
        return colorSchemeHelpers.byName(aScheme).areEqual(a, b);
    }

    const aRGB = colorSchemeHelpers.byName(aScheme).toRgb(a!);
    const bRGB = colorSchemeHelpers.byName(bScheme).toRgb(b!);

    if (aRGB.r === bRGB.r && aRGB.g === bRGB.g && aRGB.b === bRGB.b && aRGB.a === bRGB.a) {
        return true;
    }

    return false;
}

/**
 * Given constant saturation and luminance values, will generate and return count number of colors
 * with the hue equally spaced between each color.
 *
 * @param sat - saturation value
 * @param lum - luminance value
 * @param count - The number of colors to generate
 * @returns HSL[]
 */
export function generateHSLColorPaletteUsingSatAndLum(sat: number, lum: number, count: number): HSL[] {
    const result: HSL[] = [];

    for (let index = 0; index < count; index++) {
        result.push({
            h: Math.floor((360 / count) * index),
            l: lum,
            s: sat,
        });
    }

    return result;
}

// Based on https://stackoverflow.com/a/5624139
export function rgbToHex(r: number, g: number, b: number): string {
    const red = Math.max(Math.min(Math.round(r), 255), 0);
    const green = Math.max(Math.min(Math.round(g), 255), 0);
    const blue = Math.max(Math.min(Math.round(b), 255), 0);
    const componentToHex = (c: number): string => {
        const hex = c.toString(16);
        return hex.length === 1 ? `0${hex}` : hex;
    };
    return `#${componentToHex(red)}${componentToHex(green)}${componentToHex(blue)}`;
}
